import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Mail, Key, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pre-joining`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_omni-solutions-1/artifacts/nc5fgtrv_Logo.jpg";

const PreJoiningLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !accessCode) {
      toast.error("Please enter both email and access code");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("access_code", accessCode);

      const response = await axios.post(`${API}/login`, formData);
      
      localStorage.setItem("candidate_token", response.data.token);
      localStorage.setItem("candidate_data", JSON.stringify(response.data.candidate));
      
      toast.success(`Welcome, ${response.data.candidate.full_name}!`);
      navigate("/pre-joining/portal");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid email or access code");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              <img src={LOGO_URL} alt="Omnilink Solutions" className="h-16 object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              Pre-Joining Portal
            </CardTitle>
            <CardDescription className="text-slate-600">
              Welcome! Please login with your credentials provided by HR
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="h-11"
                  data-testid="login-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="accessCode" className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-slate-500" />
                  Access Code
                </Label>
                <Input
                  id="accessCode"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="Enter your access code"
                  className="h-11 font-mono tracking-widest"
                  maxLength={6}
                  data-testid="login-access-code"
                />
                <p className="text-xs text-slate-500">
                  Your 6-character access code was provided by HR
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#1e3a8a] hover:bg-[#172554]"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Logging in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-5 h-5" />
                    Login to Portal
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-slate-500 mb-2">Having trouble logging in?</p>
              <a 
                href="mailto:info@omnilinksolutions.net" 
                className="text-[#1e3a8a] hover:underline text-sm font-medium"
              >
                Contact HR at info@omnilinksolutions.net
              </a>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <a href="/" className="text-slate-500 hover:text-slate-700 text-sm flex items-center justify-center gap-2">
            <Building2 className="w-4 h-4" />
            Back to Omnilink Solutions Website
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default PreJoiningLogin;
